import { Container, Sprite, Texture } from 'pixi.js';
import { Store } from './store/gameStore';
import { Displays } from 'constants/layout';
import { Layout } from './LayoutManager';
import { DesignType } from './gameUI/GameUI';
import { EventName } from './events/eventsName';
import { GameEvent } from './events/gameEvent';
import { ATLAS_REGISTRY, PlaceholdersAtlas } from './assets/generated';
import { getBackgroundAtlasClass } from './helpers/backgroundAtlases';

let supportWebP = true;

// Cache for additional assets (not from atlases)
let gameAssets = {} as { [key: string]: Texture };

// Track loaded card atlases for unloading when theme changes
let loadedBackAtlas: any = null;
let loadedFrontAtlases: any[] = [];

// Additional assets that are not in atlases
const firstLoad = [
    'cards/shadow',
];

const uiToLoad = [] as string[];

enum AssetStatus {
    PENDING = 'pending',
    IDLE = 'idle',
    LOADED = 'loaded',
    ERROR = 'error',
}

type AssetItem = {
    alias: string;
    src: string;
    status: AssetStatus;
};

// ============================================================================
// HELPER FUNCTIONS FOR ATLASES
// ============================================================================

/**
 * Get list of atlases for card front theme
 */
function getFrontAtlasClasses(frontTheme: string) {
    const atlases: any[] = [];
    const themePath = `front/${frontTheme}`;
    
    for (const atlasClass of Object.values(ATLAS_REGISTRY)) {
        const atlas = atlasClass as any;
        const atlasPath = atlas.NAME || atlas.ATLAS_PATH;
        
        // Check if atlas belongs to the required theme by path
        if (atlasPath && atlasPath.includes(themePath)) {
            atlases.push(atlasClass);
        }
    }
    
    console.log(`📦 Found ${atlases.length} atlases for theme: ${frontTheme}`);
    return atlases;
}

/**
 * Get atlas class for card back
 */
function getBackAtlasClass(backTheme: string) {
    // Extract number from theme name (e.g., "1_blue" -> "1")
    const themeNumber = backTheme.split('_')[0];
    const backKey = `CardsBacks${themeNumber}`;
    
    for (const [atlasName, atlasClass] of Object.entries(ATLAS_REGISTRY)) {
        if (atlasName === backKey) {
            return atlasClass;
        }
    }
    
    return null;
}

// ============================================================================
// LOADING ADDITIONAL ASSETS (not from atlases)
// ============================================================================

function addToBundle(array: AssetItem[], src: string, ext = 'webp') {
    const parts = src.split('.');
    if (parts.length === 2) {
        ext = parts[1];
        src = parts[0];
    }
    array.push({
        alias: `${src}.${ext}`,
        src: `${window.location.origin}/assets/${src}.${ext}`,
        status: AssetStatus.IDLE,
    });
}

async function loadAssets(assets: AssetItem[]): Promise<void> {
    const promises = assets.map(asset => {
        return new Promise<void>(async (resolve, reject) => {
            asset.status = AssetStatus.PENDING;

            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = asset.src;

            img.onload = () => {
                try {
                    asset.status = AssetStatus.LOADED;
                    if (asset.alias) {
                        gameAssets[asset.alias] = Texture.from(img);
                        resolve();
                    }
                } catch (err) {
                    asset.status = AssetStatus.ERROR;
                    reject(err);
                }
            };

            img.onerror = () => {
                asset.status = AssetStatus.ERROR;
                reject(new Error(`Failed to load image: ${asset.src}`));
            };
        });
    });

    await Promise.allSettled(promises);
}

export async function preLoadGameAssets(preloadCb: () => void) {
    const assets = [] as AssetItem[];

    let folder = 'desktop';
    switch (Layout.getDisplay()) {
        case Displays.MOBILE:
            folder = 'mobile';
            break;
        case Displays.TABLET:
            folder = 'tablet';
            break;
        default:
            folder = 'desktop';
            break;
    }

    const backgroundAtlas = getBackgroundAtlasClass(Store.background);
    if (backgroundAtlas && typeof backgroundAtlas.load === 'function') {
        await backgroundAtlas.load()
    }
    
    addToBundle(assets, 'loader/loader.svg');
    
    await loadAssets(assets);
    preloadCb();
}

export async function loadMainAssets(preloadCb: () => void) {
    try {

        // Load additional assets (placeholders, shadows)
        const additionalAssets = [] as AssetItem[];
        firstLoad.forEach(src => {
            addToBundle(additionalAssets, src, webpFallback('png'));
        });
        await loadAssets(additionalAssets);

        console.log('🎴 Preloading card atlases on initialization...');
        
        const frontAtlases = getFrontAtlasClasses(Store.front);
        const frontPromises = frontAtlases.map(atlas => {
            if (typeof (atlas as any).load === 'function') {
                return (atlas as any).load();
            }
            return Promise.resolve();
        });
        
        const backAtlas = getBackAtlasClass(Store.back);
        if (backAtlas && typeof (backAtlas as any).load === 'function') {
            frontPromises.push((backAtlas as any).load());
        }

        frontPromises.push(PlaceholdersAtlas.load());
        
        await Promise.all(frontPromises);
        
        loadedFrontAtlases = frontAtlases;
        loadedBackAtlas = backAtlas;
        
        console.log('✅ Card atlases loaded');

        preloadCb();
    } catch (error) {
        console.error('Error loading main assets:', error);
        preloadCb();
    }
}

export async function lateAssetLoad() {
    const uiAssets = [] as AssetItem[];
    uiToLoad.forEach(src => {
        addToBundle(uiAssets, src, webpFallback('png'));
    });
    await loadAssets(uiAssets);
    onAssetsLoadComplete();
}

export async function preloadDesignAssets(type: DesignType, cbEvent: EventName) {
    try {
        GameEvent.emit(EventName.SHOW_LOADER);

        switch (type) {
            case 'card_back':
                // Unload previous card back atlas
                if (loadedBackAtlas && typeof loadedBackAtlas.unload === 'function') {
                    console.log('🗑️ Unloading previous card back atlas');
                    loadedBackAtlas.unload();
                }
                
                // Preload new card back through atlas class
                const backAtlas = getBackAtlasClass(Store.back);
                if (backAtlas && typeof (backAtlas as any).load === 'function') {
                    await (backAtlas as any).load();
                    loadedBackAtlas = backAtlas;
                }
                break;

            case 'card_front':
                // Unload previous card front atlases
                if (loadedFrontAtlases.length > 0) {
                    console.log('🗑️ Unloading previous card front atlases');
                    loadedFrontAtlases.forEach(atlas => {
                        if (typeof atlas.unload === 'function') {
                            atlas.unload();
                        }
                    });
                    loadedFrontAtlases = [];
                }
                
                // Preload new card fronts through atlas classes
                const frontAtlases = getFrontAtlasClasses(Store.front);
                const loadPromises = frontAtlases.map(atlas => {
                    if (typeof (atlas as any).load === 'function') {
                        return (atlas as any).load();
                    }
                    return Promise.resolve();
                });
                await Promise.all(loadPromises);
                loadedFrontAtlases = frontAtlases;
                break;
        }

        GameEvent.emit(EventName.HIDE_LOADER);
        GameEvent.emit(cbEvent);
        GameEvent.emit(EventName.DESIGN_ASSET_SET);
    } catch (error) {
        console.error('Error preloading design assets:', error);
        GameEvent.emit(EventName.HIDE_LOADER);
    }
}

/**
 * Get texture by name (from additional assets cache)
 * For card textures use auto-generated atlas classes!
 */
export async function getAsset(path: string, extensions = webpFallback('png')): Promise<Texture> {
    let ext = '';
    let extList: string[] = [];

    if (extensions) {
        extList = extensions.split('.');

        if (extList.includes('webp')) {
            ext = 'webp';
        } else {
            ext = extList[0];
        }
        ext = `.${ext}`;
    }

    const imgSrc = path + ext;
    return new Promise((resolve) => {
        if (gameAssets[imgSrc]) {
            resolve(gameAssets[imgSrc]);
            return;
        }

        const img = new Image();
        img.src = `${window.location.origin}/assets/${imgSrc}`;

        img.onload = async () => {
            await img.decode(); // Wait until image is completely ready

            const texture = Texture.from(img);
            gameAssets[img.src] = Texture.from(img);
            resolve(texture);
        };
        img.onerror = () => {
            console.error(`Failed to load image: ${img.src}`);
            resolve(Texture.EMPTY);
        };
    });
}

export async function getIcon(path: string, hasShadow = false) {
    const img = await getAsset(path, 'svg');
    const container = new Container();
    const sprite = Sprite.from(img);
    sprite.anchor.set(0.5);
    const size = sprite.bounds.maxX * 2;

    if (hasShadow) {
        const shadow = Sprite.from(img);
        shadow.anchor.set(0.5);
        container.addChild(shadow);
        shadow.tint = 0x000000;
        shadow.alpha = 0.25;
        shadow.position.set(0, 1);
        const size = shadow.bounds.maxX;

        shadow.width = size;
        shadow.height = size;
    }

    sprite.width = size / 2;
    sprite.height = size / 2;
    container.scale.set(1);

    container.addChild(sprite);
    return container;
}

let assetLoaded = false;
const assetLoadQueue = [] as (() => void)[];

function onAssetsLoadComplete() {
    assetLoaded = true;
    assetLoadQueue.forEach(cb => {
        cb();
    });
}

export function onAssetsLoad(cb: () => void) {
    if (assetLoaded) cb();
    else {
        assetLoadQueue.push(cb);
    }
}

export function webpFallback(fallback = 'png') {
    return supportWebP ? 'webp' : fallback;
}

export function isSupportWebP(cb: (support: boolean) => void) {
    var kTestImages = {
        lossy: 'UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA',
    };
    var img = new Image();
    img.onload = function () {
        supportWebP = img.width > 0 && img.height > 0;
        cb(supportWebP);
    };
    img.onerror = function () {
        supportWebP = false;
        cb(supportWebP);
    };
    img.src = 'data:image/webp;base64,' + kTestImages.lossy;
}
